/**
 * Flame coronas (lifted from the dungeon-floors prototype's `dungeon-glow.js`):
 * one instanced draw of camera-facing additive quads, one per fixture. The
 * vertex shader billboards and flickers them from a time uniform; nothing is
 * updated per frame on the CPU besides uniforms. A soft-particle test against
 * the lens's half-resolution depth snapshot (last frame's) hides a halo behind
 * nearer walls or units. Coronas on unexplored tiles are scaled to zero.
 */
import * as THREE from 'three';
import { presentation } from './constants.ts';

const vertexShader = `uniform float time,pull;
varying vec2 vUv;varying vec3 vColor;varying float vFlicker,vCenterZ;varying vec4 vClip;
void main(){
  vUv=uv*2.-1.;vColor=instanceColor;
  vec4 center=modelMatrix*instanceMatrix*vec4(0.,0.,0.,1.);
  float size=length(instanceMatrix[0].xyz);
  float phase=fract(sin(dot(center.xz,vec2(12.9898,78.233)))*43758.5453)*6.2831853;
  vFlicker=.9+.055*sin(time*7.3+phase)+.035*sin(time*17.9+phase*2.7)+.02*sin(time*31.+phase*5.1);
  vec4 mv=viewMatrix*center;vCenterZ=-mv.z;
  mv.xyz+=normalize(-mv.xyz)*pull*step(.0001,size);
  mv.xy+=position.xy*size*(.96+.06*vFlicker);
  mv.y+=size*.08;
  gl_Position=projectionMatrix*mv;vClip=gl_Position;
}`;

const fragmentShader = `#include <packing>
uniform sampler2D tDepth;uniform float cameraNear,cameraFar,haloGain,coreGain;
varying vec2 vUv;varying vec3 vColor;varying float vFlicker,vCenterZ;varying vec4 vClip;
void main(){
  float r2=dot(vUv,vUv);if(r2>1.)discard;
  float halo=exp(-r2*9.),core=exp(-r2*38.);
  vec3 c=vColor*(halo*haloGain+core*coreGain)*vFlicker;
  vec2 suv=vClip.xy/vClip.w*.5+.5;
  float sceneZ=-perspectiveDepthToViewZ(unpackRGBAToDepth(texture2D(tDepth,suv)),cameraNear,cameraFar);
  c*=smoothstep(-.55,-.2,sceneZ-vCenterZ);
  gl_FragColor=vec4(c,1.);
}`;

export type GlowFixture = { readonly x: number; readonly y: number; readonly z: number; readonly tile: number; readonly color: THREE.Color; readonly scale: number };

export type Glow = {
  readonly mesh: THREE.InstancedMesh;
  updateFog(seen: (tile: number) => boolean): void;
  update(time: number, depth: THREE.Texture | null, camera: THREE.PerspectiveCamera): void;
  dispose(): void;
};

export const createGlow = (fixtures: readonly GlowFixture[], gain: number, core: number, radius: number): Glow => {
  const geometry = new THREE.PlaneGeometry(1, 1);
  const uniforms = {
    time: { value: 0 },
    pull: { value: presentation.medium.glow.pull },
    tDepth: { value: null as THREE.Texture | null },
    cameraNear: { value: presentation.camera.near as number },
    cameraFar: { value: presentation.camera.far as number },
    haloGain: { value: gain },
    coreGain: { value: gain * core },
  };
  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    depthTest: false,
    blending: THREE.AdditiveBlending,
  });
  const mesh = new THREE.InstancedMesh(geometry, material, Math.max(1, fixtures.length));
  mesh.name = 'medium-glow';
  mesh.frustumCulled = false;
  mesh.renderOrder = presentation.medium.glow.renderOrder;
  mesh.count = fixtures.length;
  // The instance colour attribute exists from the start, so the program compiles with it.
  mesh.setColorAt(0, new THREE.Color(1, 1, 1));
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const v = new THREE.Vector3();
  const s = new THREE.Vector3();
  return {
    mesh,
    updateFog(seen) {
      fixtures.forEach((fx, i) => {
        const size = seen(fx.tile) ? fx.scale * radius : 0;
        mesh.setMatrixAt(i, m.compose(v.set(fx.x, fx.y, fx.z), q, s.set(size, size, size)));
        mesh.setColorAt(i, fx.color);
      });
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    },
    update(time, depth, camera) {
      uniforms.time.value = time;
      uniforms.tDepth.value = depth;
      uniforms.cameraNear.value = camera.near;
      uniforms.cameraFar.value = camera.far;
    },
    dispose() {
      mesh.removeFromParent();
      mesh.dispose();
      geometry.dispose();
      material.dispose();
    },
  };
};
